import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FiCalendar, FiUser, FiArrowRight, FiClock } from 'react-icons/fi';
import SectionHeading from '../components/SectionHeading';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const Blogs = () => {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language ? i18n.language.startsWith('ar') : false;
    const [blogPosts, setBlogPosts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const fallbackPosts = [
        {
            id: 1,
            title: t('blogs.fallback.post1.title'),
            excerpt: t('blogs.fallback.post1.excerpt'),
            date: 'Oct 15, 2025',
            author: t('blogs.author'),
            image: '/images/site/blog-commercial.png',
            category: t('blogs.fallback.post1.category'),
            readTime: '5'
        },
        {
            id: 2,
            title: t('blogs.fallback.post2.title'),
            excerpt: t('blogs.fallback.post2.excerpt'),
            date: 'Oct 02, 2025',
            author: t('blogs.author'),
            image: '/images/site/blog-hiddencosts.png',
            category: t('blogs.fallback.post2.category'),
            readTime: '4'
        },
        {
            id: 3,
            title: t('blogs.fallback.post3.title'),
            excerpt: t('blogs.fallback.post3.excerpt'),
            date: 'Sep 28, 2025',
            author: t('blogs.author'),
            image: '/images/site/blog-ropeaccess.png',
            category: t('blogs.fallback.post3.category'),
            readTime: '4'
        }
    ];

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const response = await fetch('/api/blogs/read.php');
                if (response.ok) {
                    const data = await response.json();
                    if (data.records && data.records.length > 0) {
                        setBlogPosts(data.records);
                    } else {
                        setBlogPosts(fallbackPosts);
                    }
                } else {
                    setBlogPosts(fallbackPosts);
                }
            } catch (error) {
                setBlogPosts(fallbackPosts);
            } finally {
                setIsLoading(false);
            }
        };

        fetchBlogs();
    }, [i18n.language]);

    return (
        <div className="w-full">
            <SEO
                title={t('seo.blogs.title')}
                description={t('seo.blogs.description')}
                keywords="cleaning blog bahrain, workplace hygiene tips, facility management insights, rope access benefits"
            />

            {/* Hero Banner */}
            <section className="relative pt-28 pb-12 md:pt-36 md:pb-16 overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src="/images/site/blog-insights-hero.jpg"
                        alt="PrimeForce Blogs & Insights"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/75 to-black/85"></div>
                </div>

                <div className="container-custom relative z-10 text-center">
                    <motion.span
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="inline-block px-4 py-1.5 bg-accent-500/20 text-accent-300 rounded-full text-xs sm:text-sm font-semibold mb-4 border border-accent-500/30 backdrop-blur-sm"
                    >
                        {t('blogs.hero.badge')}
                    </motion.span>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight"
                    >
                        {t('blogs.hero.titleStart')} <span className="text-accent-400">{t('blogs.hero.titleAccent')}</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="text-sm sm:text-base md:text-lg text-gray-200 max-w-3xl mx-auto leading-relaxed"
                    >
                        {t('blogs.hero.subtext')}
                    </motion.p>
                </div>
            </section>

            {/* Blog Grid */}
            <section className="section-padding bg-gray-50">
                <div className="container-custom">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {blogPosts.map((post, index) => {
                            const isFallback = post.id === 1 || post.id === 2 || post.id === 3 || post.id === '1' || post.id === '2' || post.id === '3';
                            const activeTitle = isArabic
                                ? (isFallback ? t(`blogs.fallback.post${post.id}.title`) : (post.title_ar || post.title))
                                : post.title;
                            const activeExcerpt = isArabic
                                ? (isFallback ? t(`blogs.fallback.post${post.id}.excerpt`) : (post.excerpt_ar || post.excerpt))
                                : post.excerpt;
                            const activeCategory = isArabic
                                ? (isFallback ? t(`blogs.fallback.post${post.id}.category`) : (post.category_ar || post.category))
                                : post.category;
                            const rawReadTime = String(post.readTime || '5').replace(/[^0-9]/g, '');
                            const displayReadTime = `${rawReadTime} ${t('blogs.readTimeSuffix')}`;
                            const displayAuthor = isArabic ? t('blogs.author') : (post.author || 'PrimeForce Team');

                            return (
                                <motion.article
                                    key={post.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 group flex flex-col justify-between"
                                >
                                    <Link to={`/blogs/${post.id}`} className="block h-full flex flex-col justify-between">
                                        <div>
                                            <div className="relative h-48 overflow-hidden">
                                                <div className="absolute top-4 start-4 z-10">
                                                    <span className="bg-accent-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-md">
                                                        {activeCategory}
                                                    </span>
                                                </div>
                                                <img
                                                    src={post.image}
                                                    alt={activeTitle}
                                                    loading="lazy"
                                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                            </div>
                                            <div className="p-6">
                                                <div className="flex items-center gap-4 text-xs sm:text-sm text-gray-500 mb-3 flex-wrap">
                                                    <div className="flex items-center gap-1">
                                                        <FiCalendar />
                                                        <span>{post.date}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <FiUser />
                                                        <span>{displayAuthor}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <FiClock />
                                                        <span>{displayReadTime}</span>
                                                    </div>
                                                </div>
                                                <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                                                    {activeTitle}
                                                </h3>
                                                <p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed text-sm">
                                                    {activeExcerpt}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="p-6 pt-0">
                                            <span className="inline-flex items-center gap-2 text-primary-600 font-semibold group-hover:gap-3 transition-all text-sm">
                                                <span>{t('blogs.readMore')}</span>
                                                <FiArrowRight className="rtl:rotate-180 flex-shrink-0" />
                                            </span>
                                        </div>
                                    </Link>
                                </motion.article>
                            );
                        })}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Blogs;
